import { store } from "#puf/store/index.ts";
import { z } from "zod";

/**
 * Catalog of action callbacks referenceable from user-built state machines.
 * Transitions and state nodes reference these by id; xstate resolves at machine
 * build time via the `actions` registry passed to `setup()`.
 *
 * Param shapes are validated per-action via zod; invalid params skip the side-
 * effect (no-op). Catalog growth is incremental — v1 set covers basic playback
 * and frame/layer manipulation. Add entries as needs surface.
 */

interface ActionArgs {
  context: unknown;
  event: unknown;
  params?: unknown;
}

type ActionFn = (args: ActionArgs) => void;

const SetFpsParamsSchema = z.object({ n: z.number().int().min(1).max(60) });
const SetActiveLayerParamsSchema = z.object({ id: z.string().min(1) });

const pause: ActionFn = () => {
  // Phase 5 wires the playback actor; for now this is a no-op recorded for
  // schema validation. The editor surfaces this as a known action id before
  // the runtime hookup lands.
  if (import.meta.env.DEV) console.debug("[catalog/actions] pause (no-op until Phase 5)");
};

const play: ActionFn = () => {
  if (import.meta.env.DEV) console.debug("[catalog/actions] play (no-op until Phase 5)");
};

const loopReset: ActionFn = () => {
  // Reset the active frame to the start of the current state's range. Without
  // a runtime "current state" link (Phase 5), we fall back to frame 0.
  store.trigger["activeFrame.set"]({ index: 0 });
};

const setFps: ActionFn = ({ params }) => {
  const r = SetFpsParamsSchema.safeParse(params);
  if (!r.success) {
    if (import.meta.env.DEV) console.debug("[catalog/actions] setFps invalid params:", params, r.error.issues);
    return;
  }
  store.trigger["fps.set"]({ fps: r.data.n });
};

const setActiveLayer: ActionFn = ({ params }) => {
  const r = SetActiveLayerParamsSchema.safeParse(params);
  if (!r.success) {
    if (import.meta.env.DEV) {
      console.debug("[catalog/actions] setActiveLayer invalid params:", params, r.error.issues);
    }
    return;
  }
  store.trigger["activeLayerId.set"]({ id: r.data.id });
};

export const actionCatalog: Record<string, ActionFn> = {
  pause,
  play,
  loopReset,
  setFps,
  setActiveLayer,
};

export const actionCatalogIds: ReadonlySet<string> = new Set(Object.keys(actionCatalog));
